import React, { useEffect, useState, useRef } from 'react';
import PdfViewer from '../PdfViewer.js';
import { Dropdown, Spinner } from 'react-bootstrap';
import i18next from "../../i18n.js";
import { acceptImageFileString, fileLoadTypes, signatureStates, formModes } from '../../constants.js';
import { base64toBlob, getDodumentFiles, getFileIconUrl } from '../helpers/HelperFunctions.js';
import { unknownErrorMessageDialog, messageDialog, yesNoDialog } from '../ModalDialog.js';
import AttachmentsForm from './AttachmentsForm.js';
import Signature from './Signature.js';

const FileContentForm = (props) => {
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [filesData, setFilesData] = useState(null);
    const [associations, setAssociations] = useState(null);
    const [currentIndex, setCurrentIndex] = useState(props.selectedIndex ?? 0);
    const [signatures, setSignatures] = useState([]);
    const acceptImageTypes = acceptImageFileString.split(',');
    const originalFiles = useRef({});
    const signedFiles = useRef({});

    useEffect(() => {
        if (props.docId) {
            fetch(`/api/File/GetSignatures/${props.docId}`)
                .then(async (response) => {
                    if (!response.ok) {
                        unknownErrorMessageDialog();
                    }
                    else {
                        const result = await response.json();
                        if (result?.length > 0) {
                            setSignatures(result);
                        }
                        else {
                            setLoading(false);
                            setNotFound(true);
                        }
                    }
                });
        }
    }, [props.docId])

    useEffect(() => {
        if (props.associations && ifNeedUpdateAssociations(props.associations)) {
            setAssociations(props.associations);
        }
    }, [props.associations]);

    useEffect(() => {
        if (associations) {
            getFilesData().then(filesData => {
                if (filesData && filesData.length > 0) {
                    setFilesData(filesData);
                    setLoading(false);
                }
                else {
                    setFilesData(null);
                    setLoading(false);
                    setNotFound(true);
                }
            });
            if (props.onAssociationsChanged) props.onAssociationsChanged(associations);
        }
    }, [associations]);

    useEffect(() => {
        return () => {
            if (filesData) {
                filesData.forEach(dataItem => {
                    if (dataItem.content) {
                        window.URL.revokeObjectURL(dataItem.content);
                    }
                });
            }
        }
    }, [filesData]);

    useEffect(() => {
        return () => {
            Object.keys(originalFiles.current).forEach(key => {
                window.URL.revokeObjectURL(originalFiles.current[key].fileUrl);
            });
            Object.keys(signedFiles.current).forEach(key => {
                window.URL.revokeObjectURL(signedFiles.current[key].fileUrl);
            });
        }
    }, []);

    const ifNeedUpdateAssociations = (newAssociations) => {
        if (!newAssociations || !associations) return true;
        let result = false;

        ['mainContent', 'additionalDocuments'].forEach(key => {
            if ((!newAssociations[key] && associations[key]) || (newAssociations[key] && !associations[key])) {
                result = true;
            }
            if (newAssociations[key] && associations[key]) {
                if (newAssociations[key].length !== associations[key].length) {
                    result = true;
                }
                newAssociations[key].forEach(item => {
                    if (!associations[key].includes(item)) {
                        result = true;
                    }
                });
            }
        });
        return result;
    }

    const getFilesData = async () => {
        if (associations?.mainContent?.length > 0 || associations?.additionalDocuments?.length > 0) {
            const loadInfos = [];

            let result = structuredClone(filesData) ?? [];
            result = result.filter(r => associations?.mainContent?.includes(r) || associations?.additionalDocuments?.includes(r));

            if (associations.mainContent?.length > 0) {
                if (!result.some(item => item.nodeRef === associations.mainContent[0])) {
                    loadInfos.push({
                        nodeRef: associations.mainContent[0],
                        loadAsPdf: true
                    });
                }
            }
            if (associations.additionalDocuments?.length > 0) {
                associations.additionalDocuments.forEach(docNodeRef => {
                    if (!result.some(item => item.nodeRef === docNodeRef)) {
                        loadInfos.push({
                            nodeRef: docNodeRef,
                            loadAsPdf: true
                        });
                    }
                });
            }

            const data = loadInfos.length > 0 ? await getDodumentFiles('/api/file/GetSignedFiles', loadInfos) : [];

            if (data) {
                data.forEach(item => result.push(item));
            }
            return result;
        }
        return null;
    }

    const onNavigate = (index) => {
        setCurrentIndex(index);
    }

    const getFileViewer = (fileData) => {
        if (fileData.file.mimeType === 'application/pdf') {
            return <PdfViewer src={fileData.file.content} />;
        }
        else if (acceptImageTypes.includes(fileData.file.mimeType)) {
            return <img className='img-file' src={fileData.file.content} />
        }
        else if (fileData.file.mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
            return <div className="not-viewed">{i18next.t("document.notViewedDocxMessage")}</div>
        }
        else {
            return <div className="not-viewed">{i18next.t("document.notViewedMessage")}</div>
        }
    }

    const onDownload = async (loadType) => {
        let fileName = '';
        let fileUrl = '';
        switch (loadType) {
            case fileLoadTypes.original:
                if (originalFiles.current[filesData[currentIndex].nodeRef]) {
                    fileName = originalFiles.current[filesData[currentIndex].nodeRef].fileName;
                    fileUrl = originalFiles.current[filesData[currentIndex].nodeRef].fileUrl;
                }
                else {
                    await fetch(`/api/file/get?nodeRefs=${filesData[currentIndex].nodeRef}`)
                        .then(async (response) => {
                            if (!response.ok) {
                                unknownErrorMessageDialog();
                            }
                            else {
                                const files = await response.json();
                                if (files[0].content) {
                                    fileName = files[0].fileName;
                                    fileUrl = window.URL.createObjectURL(base64toBlob(files[0].content, files[0].mimeType));
                                    originalFiles.current[filesData[currentIndex].nodeRef] = {
                                        fileName: fileName,
                                        fileUrl: fileUrl
                                    }
                                }
                            }
                        });
                }
                break;
            case fileLoadTypes.view:
                fileUrl = filesData[currentIndex].file.content;
                fileName = filesData[currentIndex].file.fileName;
                break;
            case fileLoadTypes.withSigns:
                let notFound = true;
                if (signedFiles.current[filesData[currentIndex].nodeRef]) {
                    notFound = false;
                }
                else {
                    await fetch(`/api/file/GetP7s?nodeRefs=${filesData[currentIndex].nodeRef}`)
                        .then(async (response) => {
                            if (response.ok) {
                                const files = await response.json();
                                if (files[0].content) {
                                    signedFiles.current[filesData[currentIndex].nodeRef] = {
                                        fileName: files[0].fileName,
                                        fileUrl: window.URL.createObjectURL(base64toBlob(files[0].content, files[0].mimeType))
                                    }
                                    notFound = false;
                                }
                            }
                        });
                }
                if (notFound) {
                    messageDialog(i18next.t('error'), i18next.t('document.signNotFoundMessage'));
                }
                else {
                    const a = document.createElement('a');
                    a.href = signedFiles.current[filesData[currentIndex].nodeRef].fileUrl;
                    a.download = signedFiles.current[filesData[currentIndex].nodeRef].fileName;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);

                    fileUrl = filesData[currentIndex].file.content;
                    fileName = filesData[currentIndex].file.fileName;
                }
                break;
        }
        if (fileUrl && fileName) {
            const a = document.createElement('a');
            a.href = fileUrl;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        }
    }

    const onAttachmentAdded = (e) => {
        if (props.onChange) props.onChange(e);
        if (e.target.value && e.target.value.length > 0) {
            fetch(`/api/document/get/${props.docId}`, { method: 'GET', headers: { 'Content-Type': 'application/json' } })
                .then((response) => {
                    if (response.ok) {
                        return response.json();
                    }
                    else if (response.status === 404) {
                        messageDialog(i18next.t('error'), i18next.t('documentNotFound'), () => { navigate(urls.documentsPage) });
                    }
                    else {
                        unknownErrorMessageDialog(() => { navigate(urls.documentsPage) });
                    }
                })
                .then((data) => {
                    if (data) {
                        setAssociations(data.associations);
                    }
                });
        }
    }

    const onDeleteFile = () => {
        const nodeRef = filesData[currentIndex].nodeRef;
        yesNoDialog(i18next.t('document.deleteFileAcception'), i18next.t('document.acceptDeleteFileMessage'), async () => {
            fetch(`/api/document/RemoveAdditionalFile?id=${props.docId}&fileNodeRef=${nodeRef}`, { method: 'DELETE', headers: { 'Content-Type': 'application/json' } })
                .then((response) => {
                    if (response.ok) {
                        onNavigate(0);
                        setFilesData(prevFilesData => prevFilesData.filter(item => item.nodeRef !== nodeRef));
                        const updatedAssociations = structuredClone(associations);
                        updatedAssociations.additionalDocuments = updatedAssociations.additionalDocuments.filter(item => item !== nodeRef);
                        setAssociations(updatedAssociations);
                    }
                    else {
                        unknownErrorMessageDialog();
                    }
                });
        });
    }

    const getSignatures = (index) => {
        const fileData = filesData[index];
        const signedFile = signatures.find(f => f.nodeRef === fileData.nodeRef);
        if (signedFile) {
            return signedFile.signList.signatures;
        }

        return [];
    }

    if (loading) {
        return <div className="file-contaner">
            {!props.hideLoader && <Spinner animation="border" variant="primary" />}
        </div>
    }
    else {
        return <div className="file-contaner">
            <div className="form-group">
                <div className='label-contaner'>
                    <label>{i18next.t("document.documentLabel")}</label>
                </div>
            </div>
            {filesData ?
                [
                    <div className="file-navigation" key='navigation'>
                        {filesData.map((item, index) => {
                            const signatures = getSignatures(index);
                            const signed = signatures.some(s => s.state === signatureStates.signed);
                            return (<div className={'item' + (index === currentIndex ? ' selected' : '') + (signed ? ' signed' : '')} onClick={() => onNavigate(index)} key={index} >
                                <div className='icon'><img src={getFileIconUrl(item.file.fileName)} /></div>
                                <div className='title'>{item.file.fileName}</div>
                                {signed && [<div className='sign-icon' key='sign-icon'>
                                    <img src="img/signed.svg" />
                                </div>,
                                <Dropdown key='expand-sign-btn'>
                                    <Dropdown.Toggle id={"expand-btn-" + index}>
                                        
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                        {signatures.map(signature => <Signature signature={signature} />)}
                                    </Dropdown.Menu>
                                </Dropdown>]
                                }
                            </div>);
                        })}
                    </div>,
                    <div className="file-header" key='header'>
                        <div className="title">{filesData[currentIndex].file.fileName}</div>
                        {props.canAdd && filesData[currentIndex].nodeRef !== associations.mainContent[0] && <div className='remove-btn' onClick={() => onDeleteFile()} />}
                        <Dropdown>
                            <Dropdown.Toggle id="file-load-btn">
                                {i18next.t("download")}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item onClick={() => onDownload(fileLoadTypes.original)}>
                                    {i18next.t(`fileLoadTypes.${fileLoadTypes.original}`)}
                                </Dropdown.Item>
                                <Dropdown.Item onClick={() => onDownload(fileLoadTypes.view)}>
                                    {i18next.t(`fileLoadTypes.${fileLoadTypes.view}`)}
                                </Dropdown.Item>
                                <Dropdown.Item onClick={() => onDownload(fileLoadTypes.withSigns)}>
                                    {i18next.t(`fileLoadTypes.${fileLoadTypes.withSigns}`)}
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                ] :
                <div className="file-header">
                    {notFound && <div className="not-found">{i18next.t("document.fileNotFound")}</div>}
                </div>
            }
            {filesData && getFileViewer(filesData[currentIndex])}
            {props.canAdd && <AttachmentsForm name="sx:attachments" docId={props.docId} onChange={onAttachmentAdded} hideTable={true} fromMode={formModes.read} />}
        </div>
    }
}

export default FileContentForm;